import { axios } from '@/axios';
import { config } from '@/config';
import {
  SanitizeReviewContent,
  SanitizeReviewLesson,
  SanitizeReviewLessonFilters,
  SanitizeReviewPage,
  SanitizeReviewRun,
  SanitizeReviewRunFilters,
} from '@/models/sanitizeReview';
import { useQuery } from '@tanstack/react-query';

const reviewBaseUrl = `${config.backend}/sanitize-review`;

export const sanitizeReviewQueryKeys = {
  content: (runId: string, lessonItemId: string) =>
    ['sanitize-review', 'content', runId, lessonItemId] as const,
  lessons: (runId: string, filters: SanitizeReviewLessonFilters) =>
    ['sanitize-review', 'lessons', runId, filters] as const,
  runs: (filters: SanitizeReviewRunFilters) =>
    ['sanitize-review', 'runs', filters] as const,
};

export async function getSanitizeReviewRuns(
  filters: SanitizeReviewRunFilters,
  signal?: AbortSignal
) {
  const response = await axios.get<SanitizeReviewPage<SanitizeReviewRun>>(
    `${reviewBaseUrl}/runs`,
    { params: filters, signal }
  );
  return response.data;
}

export async function getSanitizeReviewLessons(
  runId: string,
  filters: SanitizeReviewLessonFilters,
  signal?: AbortSignal
) {
  const response = await axios.get<SanitizeReviewPage<SanitizeReviewLesson>>(
    `${reviewBaseUrl}/runs/${encodeURIComponent(runId)}/lessons`,
    { params: filters, signal }
  );
  return response.data;
}

export async function getSanitizeReviewContent(
  runId: string,
  lessonItemId: string,
  signal?: AbortSignal
) {
  const response = await axios.get<SanitizeReviewContent>(
    `${reviewBaseUrl}/runs/${encodeURIComponent(
      runId
    )}/lessons/${encodeURIComponent(lessonItemId)}/content`,
    { signal }
  );
  return response.data;
}

export function useSanitizeReviewRuns(filters: SanitizeReviewRunFilters = {}) {
  return useQuery({
    keepPreviousData: true,
    queryFn: ({ signal }) => getSanitizeReviewRuns(filters, signal),
    queryKey: sanitizeReviewQueryKeys.runs(filters),
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });
}

export function useSanitizeReviewLessons(
  runId: string | undefined,
  filters: SanitizeReviewLessonFilters
) {
  return useQuery({
    enabled: Boolean(runId),
    keepPreviousData: true,
    queryFn: ({ signal }) =>
      getSanitizeReviewLessons(runId as string, filters, signal),
    queryKey: sanitizeReviewQueryKeys.lessons(runId || '', filters),
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });
}

export function useSanitizeReviewContent(
  runId: string | undefined,
  lessonItemId: string | null,
  enabled = true
) {
  return useQuery({
    enabled: Boolean(enabled && runId && lessonItemId),
    queryFn: ({ signal }) =>
      getSanitizeReviewContent(runId as string, lessonItemId as string, signal),
    queryKey: sanitizeReviewQueryKeys.content(runId || '', lessonItemId || ''),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}
